import { useParams, Link } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { listings, type Listing } from '../api/client';
import { useSavedListings } from '../context/SavedListingsContext';
import { getAmenityLabel } from '../data/amenities';
import EmbeddedMap from '../components/EmbeddedMap';

function formatDate(iso: string, locale: string): string {
  try {
    const d = new Date(iso);
    const loc = locale.startsWith('ar') ? 'ar-EG' : 'en-CA';
    return d.toLocaleDateString(loc, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '—';
  }
}

function getRelativeTimeKey(iso: string): { key: string; n?: number } | null {
  try {
    const d = new Date(iso);
    const now = new Date();
    const sec = Math.floor((now.getTime() - d.getTime()) / 1000);
    if (sec < 60) return { key: 'time.justNow' };
    if (sec < 3600) return { key: 'time.minAgo', n: Math.floor(sec / 60) };
    if (sec < 86400) return { key: 'time.hoursAgo', n: Math.floor(sec / 3600) };
    if (sec < 2592000) return { key: 'time.daysAgo', n: Math.floor(sec / 86400) };
    return null;
  } catch {
    return null;
  }
}

function formatPrice(value: number | undefined | null, currency: string, locale: string): string {
  if (value == null) return '—';
  const loc = locale.startsWith('ar') ? 'ar-EG' : 'en-CA';
  return new Intl.NumberFormat(loc, {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

const PLACEHOLDER_IMAGE =
  'data:image/svg+xml,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300"><rect fill="%23e5e7eb" width="400" height="300"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%239ca3af" font-family="sans-serif" font-size="18">No photo</text></svg>'
  );

/** Country code to name for map search (improves results). */
const COUNTRY_MAP: Record<string, string> = {
  AE: 'United Arab Emirates',
  SA: 'Saudi Arabia',
  EG: 'Egypt',
  KW: 'Kuwait',
  BH: 'Bahrain',
  OM: 'Oman',
  QA: 'Qatar',
  CA: 'Canada',
  GB: 'United Kingdom',
};

function getMapSearchQuery(listing: Listing): string {
  const loc = listing.location;
  if (!loc) return '';
  const address = listing.address?.trim();
  const parts = address
    ? [address, loc.area, loc.city].filter(Boolean)
    : [loc.area, loc.city].filter(Boolean);
  const countryName = (loc.country && COUNTRY_MAP[loc.country]) || loc.country || '';
  const all = [...parts, countryName].filter(Boolean);
  return all.join(', ') || '';
}

function getMapUrl(listing: Listing): string {
  const query = getMapSearchQuery(listing);
  if (!query) return '';
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

export default function ListingDetail() {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const [listing, setListing] = useState<Listing | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isSaved, toggle } = useSavedListings();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [inquirySent, setInquirySent] = useState(false);
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [callbackOpen, setCallbackOpen] = useState(false);
  const [callbackName, setCallbackName] = useState('');
  const [callbackPhone, setCallbackPhone] = useState('');
  const [callbackEmail, setCallbackEmail] = useState('');
  const [callbackSubmitting, setCallbackSubmitting] = useState(false);
  const [callbackDone, setCallbackDone] = useState(false);
  const locale = i18n.language || 'en';
  const mapSectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!id) return;
    setError(null);
    const mediaPromise = listings.getMediaUrls(id).then((r) => r.urls || []).catch(() => [] as string[]);
    listings
      .get(id)
      .then(async (l) => {
        setListing(l);
        setMessage(t('listingDetail.messagePlaceholder'));
        const fromListing = l.mediaUrls && l.mediaUrls.length > 0 ? l.mediaUrls : [];
        const httpKeys = (l.mediaKeys || []).filter((k): k is string => typeof k === 'string' && k.startsWith('http'));
        const fromMediaEndpoint = await mediaPromise;
        const urls = fromListing.length > 0 ? fromListing : fromMediaEndpoint.length > 0 ? fromMediaEndpoint : httpKeys;
        setImageUrls(urls);
      })
      .catch(() => {
        setListing(null);
        setError('');
        setImageUrls([]);
      })
      .finally(() => setLoading(false));
  }, [id, t]);

  const handleShare = () => {
    const shareTitle = listing ? (locale === 'ar' && listing.titleAr ? listing.titleAr : listing.titleEn || '') : '';
    if (typeof navigator !== 'undefined' && navigator.share) {
      navigator.share({
        title: shareTitle,
        text: `${shareTitle} – ${listing?.currency} ${listing?.price?.toLocaleString()}`,
        url: window.location.href,
      }).catch(() => {});
    } else {
      navigator.clipboard?.writeText(window.location.href);
    }
  };

  const handleSubmitInquiry = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !listing) return;
    setSubmitting(true);
    try {
      await listings.createInquiry(id, { name, email, phone, message, source: 'listing_detail' });
      setInquirySent(true);
    } catch {
      setSubmitting(false);
    }
  };

  const handleRequestCallback = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !listing) return;
    setCallbackSubmitting(true);
    try {
      await listings.createInquiry(id, {
        name: callbackName.trim(),
        email: callbackEmail.trim(),
        phone: callbackPhone.trim(),
        message: 'Requested callback',
        source: 'callback',
      });
      setCallbackDone(true);
    } catch {
      setCallbackSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '2rem 1rem', textAlign: 'center', color: '#475569' }}>
        {t('listingDetail.loadingProperty')}
      </div>
    );
  }

  if (error !== null || !listing) {
    return (
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '2rem 1rem' }}>
        <p style={{ color: '#EF4444', marginBottom: '1rem' }}>{error || t('listingDetail.listingNotFound')}</p>
        <Link to="/listings" style={{ color: '#0d9488', fontWeight: 600 }}>
          ← {t('common.backToSearch')}
        </Link>
      </div>
    );
  }

  const locationLine = [listing.location?.area, listing.location?.city].filter(Boolean).join(', ') || listing.location?.country || '—';
  const statusLabel = listing.type === 'sale' ? t('common.forSale') : listing.type === 'rent' ? t('common.forRent') : t('common.shortTerm');
  const saved = isSaved(listing.listingId);
  const displayTitle = (locale === 'ar' && listing.titleAr) ? listing.titleAr : (listing.titleEn || '');
  const displayDescription = (locale === 'ar' && listing.descriptionAr) ? listing.descriptionAr : (listing.descriptionEn || '');
  const rt = getRelativeTimeKey(listing.createdAt);
  const addedLabel = rt ? t(rt.key, { n: rt.n }) : formatDate(listing.createdAt, locale);

  const breadcrumbParts: { label: string; to: string | null }[] = [
    { label: t('common.home'), to: '/' },
    ...(listing.location?.city ? [{ label: listing.location.city, to: `/?location=${encodeURIComponent(listing.location.city)}` }] : []),
    ...(listing.location?.area ? [{ label: listing.location.area, to: null }] : []),
    { label: displayTitle, to: null },
  ];

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 1rem 2rem' }}>
      {/* Back + Breadcrumb */}
      <div style={{ marginBottom: '1rem', fontSize: '0.9rem', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
        <Link to="/listings" style={{ color: '#0d9488', fontWeight: 500, textDecoration: 'none', marginRight: '1rem' }}>
          ← {t('common.backToSearch')}
        </Link>
        <span style={{ color: '#94A3B8' }}>
          {breadcrumbParts.map((b, i) => (
            <span key={i}>
              {i > 0 && <span style={{ margin: '0 0.35rem' }}>›</span>}
              {b.to ? (
                <Link to={b.to} style={{ color: '#0d9488', textDecoration: 'none' }}>{b.label}</Link>
              ) : (
                <span style={{ color: '#475569' }}>{b.label}</span>
              )}
            </span>
          ))}
        </span>
      </div>

      {/* Gallery */}
      {imageUrls.length > 0 ? (
        <div style={{ marginBottom: '1.5rem', borderRadius: 12, overflow: 'hidden', background: '#F1F5F9', position: 'relative' }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {imageUrls.map((src, i) => (
              <button
                key={i}
                type="button"
                onClick={() => { setLightboxIndex(i); setLightboxOpen(true); }}
                style={{
                  border: 0,
                  padding: 0,
                  cursor: 'pointer',
                  background: 'transparent',
                  overflow: 'hidden',
                  flex: 1,
                  minWidth: 200,
                  height: 220,
                  outline: 'none',
                }}
              >
                <img
                  src={src}
                  alt={`${displayTitle} ${i + 1}`}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = PLACEHOLDER_IMAGE; }}
                />
              </button>
            ))}
          </div>
          {imageUrls.length > 1 && (
            <button
              type="button"
              onClick={() => { setLightboxIndex(0); setLightboxOpen(true); }}
              style={{
                position: 'absolute',
                bottom: 12,
                right: 12,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 12px',
                borderRadius: 8,
                background: 'rgba(0,0,0,0.6)',
                color: '#fff',
                fontSize: '0.875rem',
                border: 0,
                cursor: 'pointer',
                outline: 'none',
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <path d="m21 15-5-5L5 21" />
              </svg>
              {t('listingDetail.seeAllPhotos', { n: imageUrls.length })}
            </button>
          )}
        </div>
      ) : (
        <div style={{ marginBottom: '1.5rem', borderRadius: 12, overflow: 'hidden', background: '#E2E8F0', minHeight: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ textAlign: 'center', color: '#475569', padding: '2rem' }}>
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ margin: '0 auto 0.75rem', opacity: 0.6 }} aria-hidden>
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <path d="m21 15-5-5L5 21" />
            </svg>
            <p style={{ margin: 0, fontSize: '1rem' }}>{t('listingDetail.noPhotosYet')}</p>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightboxOpen && imageUrls.length > 0 && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={t('listingDetail.photoGallery')}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.92)',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => setLightboxOpen(false)}
        >
          <button
            type="button"
            onClick={() => setLightboxOpen(false)}
            aria-label={t('listingDetail.galleryClose')}
            style={{
              position: 'absolute',
              top: 16,
              right: 16,
              width: 44,
              height: 44,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.2)',
              border: 0,
              color: '#fff',
              fontSize: '1.5rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: 1,
              outline: 'none',
            }}
          >
            ×
          </button>
          <button
            type="button"
            aria-label={t('listingDetail.galleryPrev')}
            onClick={(e) => { e.stopPropagation(); setLightboxIndex((prev) => (prev === 0 ? imageUrls.length - 1 : prev - 1)); }}
            style={{
              position: 'absolute',
              left: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 48,
              height: 48,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.2)',
              border: 0,
              color: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              outline: 'none',
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <img
            src={imageUrls[lightboxIndex]}
            alt={`${displayTitle} photo ${lightboxIndex + 1}`}
            style={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain' }}
            onClick={(e) => e.stopPropagation()}
            onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = PLACEHOLDER_IMAGE; }}
          />
          <button
            type="button"
            aria-label={t('listingDetail.galleryNext')}
            onClick={(e) => { e.stopPropagation(); setLightboxIndex((prev) => (prev === imageUrls.length - 1 ? 0 : prev + 1)); }}
            style={{
              position: 'absolute',
              right: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 48,
              height: 48,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.2)',
              border: 0,
              color: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              outline: 'none',
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
          <p style={{ position: 'absolute', bottom: 24, color: 'rgba(255,255,255,0.9)', fontSize: '0.875rem' }}>
            {lightboxIndex + 1} / {imageUrls.length}
          </p>
        </div>
      )}

      <div className="listing-detail-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: '2rem', alignItems: 'start' }}>
        {/* Left: property info */}
        <div>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#0F172A', marginBottom: 4 }}>{displayTitle}</h1>
              <p style={{ color: '#475569', fontSize: '1rem' }}>{locationLine}</p>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {getMapSearchQuery(listing) ? (
                <>
                  <button
                    type="button"
                    onClick={() => mapSectionRef.current?.scrollIntoView({ behavior: 'smooth' })}
                    style={{
                      padding: '0.5rem 1rem',
                      border: '1px solid #d1d5db',
                      borderRadius: 8,
                      background: '#fff',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      color: '#374151',
                      cursor: 'pointer',
                    }}
                  >
                    {t('listingDetail.viewOnMap')}
                  </button>
                  <a
                    href={getMapUrl(listing)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      padding: '0.5rem 1rem',
                      border: '1px solid #d1d5db',
                      borderRadius: 8,
                      background: '#fff',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      color: '#374151',
                      cursor: 'pointer',
                      textDecoration: 'none',
                    }}
                  >
                    {t('listingDetail.openInGoogleMaps')}
                  </a>
                </>
              ) : (
                <span
                  title={t('listingDetail.addLocationToViewOnMap')}
                  style={{
                    padding: '0.5rem 1rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: 8,
                    background: '#f9fafb',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: '#9ca3af',
                    cursor: 'not-allowed',
                  }}
                >
                  {t('listingDetail.viewOnMap')}
                </span>
              )}
              <button
                type="button"
                onClick={handleShare}
                style={{
                  padding: '0.5rem 1rem',
                  border: '1px solid #d1d5db',
                  borderRadius: 8,
                  background: '#fff',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: '#374151',
                  cursor: 'pointer',
                }}
              >
                {t('common.share')}
              </button>
              <button
                type="button"
                onClick={() => toggle(listing)}
                aria-label={saved ? t('listingDetail.removeFromSaved') : t('listingDetail.saveListing')}
                style={{
                  padding: '0.5rem 1rem',
                  border: '1px solid #d1d5db',
                  borderRadius: 8,
                  background: '#fff',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: saved ? '#0f766e' : '#374151',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill={saved ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" aria-hidden>
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
                {saved ? t('common.savedListing') : t('common.save')}
              </button>
            </div>
          </div>

          <div style={{ marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '1px solid #E2E8F0' }}>
            <p style={{ fontSize: '1.5rem', fontWeight: 700, color: '#0d9488', marginBottom: 8 }}>
              {formatPrice(listing.price, listing.currency ?? 'USD', locale)}
            </p>
            {listing.type === 'rent' && listing.price != null && (
              <p style={{ fontSize: '0.875rem', color: '#475569', marginBottom: 8 }}>
                est. {formatPrice(Math.round(listing.price / 12), listing.currency ?? 'USD', locale)}/mo
              </p>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '1rem', marginBottom: 8 }}>
              <span style={{
                padding: '4px 12px',
                borderRadius: 6,
                fontSize: '0.875rem',
                fontWeight: 500,
                background: '#0d9488',
                color: '#fff',
              }}>
                {statusLabel}
              </span>
              <span style={{ fontSize: '0.875rem', color: '#475569' }}>{t('common.added')} {addedLabel}</span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', fontSize: '0.95rem', color: '#475569', alignItems: 'center' }}>
              {listing.bedrooms != null && <span>{listing.bedrooms} bed</span>}
              {listing.bathrooms != null && <span>{listing.bathrooms} bath</span>}
              {listing.areaSqm != null && <span>{listing.areaSqm} m²</span>}
              {(listing.viewCount ?? 0) > 0 && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#64748B' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                  {t('common.views', { count: listing.viewCount ?? 0 })}
                </span>
              )}
            </div>
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.75rem', color: '#0F172A' }}>{t('common.details')}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '0.75rem 1.5rem' }}>
              {(listing as { unitType?: string }).unitType && (() => {
                const u = (listing as { unitType: string }).unitType;
                const key = u === 'twin_house' ? 'TwinHouse' : u === 'town' ? 'Town' : u ? u.charAt(0).toUpperCase() + u.slice(1) : '';
                return key ? (
                  <div key="unitType">
                    <span style={{ fontSize: '0.75rem', color: '#64748B' }}>{t('form.unitType')}</span>
                    <p style={{ margin: 0, fontWeight: 500 }}>{t(`form.unitType${key}`)}</p>
                  </div>
                ) : null;
              })()}
              <div>
                <span style={{ fontSize: '0.75rem', color: '#64748B' }}>{t('common.type')}</span>
                <p style={{ margin: 0, fontWeight: 500 }}>{listing.category === 'residential' ? t('common.residential') : t('common.commercial')}</p>
              </div>
              {listing.areaSqm != null && (
                <div>
                  <span style={{ fontSize: '0.75rem', color: '#64748B' }}>{t('common.size')}</span>
                  <p style={{ margin: 0, fontWeight: 500 }}>{listing.areaSqm} m²</p>
                </div>
              )}
              {listing.bedrooms != null && (
                <div>
                  <span style={{ fontSize: '0.75rem', color: '#64748B' }}>{t('common.bedrooms')}</span>
                  <p style={{ margin: 0, fontWeight: 500 }}>{listing.bedrooms}</p>
                </div>
              )}
              {listing.bathrooms != null && (
                <div>
                  <span style={{ fontSize: '0.75rem', color: '#64748B' }}>{t('common.bathrooms')}</span>
                  <p style={{ margin: 0, fontWeight: 500 }}>{listing.bathrooms}</p>
                </div>
              )}
            </div>
          </div>

          {listing.amenities && listing.amenities.length > 0 && (
            <div style={{ marginBottom: '1.5rem' }}>
              <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.75rem', color: '#0F172A' }}>{t('common.amenities')}</h2>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {listing.amenities.map((key) => (
                  <span
                    key={key}
                    style={{
                      padding: '6px 12px',
                      background: '#F1F5F9',
                      color: '#475569',
                      borderRadius: 8,
                      fontSize: '0.875rem',
                    }}
                  >
                    {getAmenityLabel(key, 'en')}
                  </span>
                ))}
              </div>
            </div>
          )}

          {displayDescription && (
            <div style={{ marginBottom: '1.5rem' }}>
              <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: 8, color: '#0F172A' }}>{t('common.description')}</h2>
              <p style={{ color: '#475569', lineHeight: 1.6, margin: 0 }}>{displayDescription}</p>
            </div>
          )}

          {getMapSearchQuery(listing) && (
            <div ref={mapSectionRef} id="listing-map" style={{ scrollMarginTop: 16 }}>
              <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.75rem', color: '#0F172A' }}>{t('listingDetail.locationOnMap')}</h2>
              <EmbeddedMap
                query={getMapSearchQuery(listing)}
                height={280}
                ariaLabel={locationLine}
              />
              <a
                href={getMapUrl(listing)}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: '0.875rem', color: '#0d9488', fontWeight: 500 }}
              >
                {t('listingDetail.openInGoogleMaps')}
              </a>
            </div>
          )}

          {(listing.contactEmail || listing.contactPhone) && (
            <div style={{ marginBottom: '1.5rem', padding: '1.25rem', background: '#f0fdfa', borderRadius: 12, border: '1px solid #99f6e4' }}>
              <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.75rem', color: '#0F172A' }}>{t('listingDetail.contactHeading')}</h2>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                {listing.contactEmail && (
                  <a
                    href={`mailto:${listing.contactEmail}`}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '10px 16px',
                      background: '#fff',
                      border: '1px solid #0d9488',
                      borderRadius: 8,
                      color: '#0d9488',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      textDecoration: 'none',
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                    {t('listingDetail.emailBtn')}
                  </a>
                )}
                {listing.contactPhone && (
                  <>
                    <a
                      href={`tel:${listing.contactPhone.replace(/\s/g, '')}`}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '10px 16px',
                        background: '#0d9488',
                        border: 0,
                        borderRadius: 8,
                        color: '#fff',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        textDecoration: 'none',
                      }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                      </svg>
                      {t('listingDetail.callBtn')}
                    </a>
                    <a
                      href={`https://wa.me/${listing.contactPhone.replace(/\D/g, '')}?text=${encodeURIComponent('Hi, I am interested in this property: ' + (listing.titleEn || '') + ' ' + window.location.href)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '10px 16px',
                        background: '#25D366',
                        border: 0,
                        borderRadius: 8,
                        color: '#fff',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        textDecoration: 'none',
                      }}
                    >
                      {t('listingDetail.whatsappBtn')}
                    </a>
                    <button
                      type="button"
                      onClick={() => setCallbackOpen(true)}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '10px 16px',
                        background: '#fff',
                        border: '1px solid #0d9488',
                        borderRadius: 8,
                        color: '#0d9488',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        cursor: 'pointer',
                      }}
                    >
                      {t('listingDetail.requestCallback')}
                    </button>
                  </>
                )}
              </div>
              {callbackOpen && (
                <div style={{ marginTop: 16, padding: 16, background: '#fff', borderRadius: 8, border: '1px solid #e5e7eb' }}>
                  {callbackDone ? (
                    <p style={{ color: '#059669', fontWeight: 500 }}>{t('listingDetail.callbackThankYou')}</p>
                  ) : (
                    <form onSubmit={handleRequestCallback}>
                      <div style={{ marginBottom: 10 }}>
                        <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: 4 }}>{t('listingDetail.fullName')} *</label>
                        <input type="text" required value={callbackName} onChange={(e) => setCallbackName(e.target.value)} placeholder={t('listingDetail.yourNamePlaceholder')} style={{ width: '100%', padding: '0.5rem', borderRadius: 6, border: '1px solid #d1d5db', boxSizing: 'border-box' }} />
                      </div>
                      <div style={{ marginBottom: 10 }}>
                        <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: 4 }}>{t('listingDetail.phone')} *</label>
                        <input type="tel" required value={callbackPhone} onChange={(e) => setCallbackPhone(e.target.value)} placeholder={t('listingDetail.phonePlaceholder')} style={{ width: '100%', padding: '0.5rem', borderRadius: 6, border: '1px solid #d1d5db', boxSizing: 'border-box' }} />
                      </div>
                      <div style={{ marginBottom: 10 }}>
                        <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: 4 }}>{t('listingDetail.email')} *</label>
                        <input type="email" required value={callbackEmail} onChange={(e) => setCallbackEmail(e.target.value)} placeholder={t('listingDetail.emailPlaceholder')} style={{ width: '100%', padding: '0.5rem', borderRadius: 6, border: '1px solid #d1d5db', boxSizing: 'border-box' }} />
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button type="submit" disabled={callbackSubmitting} style={{ padding: '0.5rem 1rem', background: '#0d9488', color: '#fff', border: 0, borderRadius: 6, fontWeight: 600, cursor: callbackSubmitting ? 'not-allowed' : 'pointer' }}>{callbackSubmitting ? t('common.loading') : t('listingDetail.requestCallbackSubmit')}</button>
                        <button type="button" onClick={() => setCallbackOpen(false)} style={{ padding: '0.5rem 1rem', background: '#f3f4f6', color: '#374151', border: 0, borderRadius: 6, cursor: 'pointer' }}>{t('form.cancel')}</button>
                      </div>
                    </form>
                  )}
                </div>
              )}
              <p style={{ marginTop: 12, fontSize: '0.875rem', color: '#475569', margin: '12px 0 0' }}>
                {listing.contactEmail && <span>{listing.contactEmail}</span>}
                {listing.contactEmail && listing.contactPhone && ' · '}
                {listing.contactPhone && <span>{listing.contactPhone}</span>}
              </p>
            </div>
          )}

          <div style={{ padding: '1rem', background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb' }}>
            <h2 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.75rem', color: '#0F172A' }}>{t('common.listingInfo')}</h2>
            <div style={{ fontSize: '0.875rem', color: '#475569' }}>
              {(listing.viewCount ?? 0) > 0 && <p style={{ margin: '4px 0' }}>{t('common.views', { count: listing.viewCount ?? 0 })}</p>}
              <p style={{ margin: '4px 0' }}>{t('common.added')}: {formatDate(listing.createdAt, locale)}</p>
              {listing.publishedAt && <p style={{ margin: '4px 0' }}>{t('common.published')}: {formatDate(listing.publishedAt, locale)}</p>}
              {listing.expiresAt && <p style={{ margin: '4px 0' }}>{t('common.expires')}: {formatDate(listing.expiresAt, locale)}</p>}
              <p style={{ margin: '4px 0' }}>{t('common.updated')}: {formatDate(listing.updatedAt, locale)}</p>
              <p style={{ margin: '4px 0' }}>{t('common.listingId')}: {listing.listingId}</p>
            </div>
          </div>
        </div>

        {/* Right: Ask About this Home */}
        <div style={{ position: 'sticky', top: 24 }}>
          <div
            style={{
              padding: '1.5rem',
              background: '#f0fdfa',
              border: '1px solid #99f6e4',
              borderRadius: 12,
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            }}
          >
            <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: 8, color: '#0F172A' }}>{t('listingDetail.askAboutHome')}</h2>
            <p style={{ fontSize: '0.875rem', color: '#475569', marginBottom: '1rem' }}>{t('listingDetail.askAboutHomeDesc')}</p>

            {listing.listingId.startsWith('lst-demo-') && (
              <p style={{ fontSize: '0.875rem', color: '#475569', marginBottom: '1rem', padding: 8, background: '#F8FAFC', borderRadius: 8 }}>
                {t('listingDetail.demoListing')}
              </p>
            )}
            {inquirySent ? (
              <div style={{ padding: '1rem', background: '#ECFDF5', borderRadius: 8, color: '#16A34A', fontWeight: 500 }}>
                {t('listingDetail.thankYou')}
              </div>
            ) : (
              <form onSubmit={handleSubmitInquiry} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: 4, color: '#374151' }}>{t('listingDetail.fullName')} *</label>
                  <input
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t('listingDetail.yourNamePlaceholder')}
                    style={{
                      width: '100%',
                      padding: '0.6rem 0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: 8,
                      fontSize: '0.9rem',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: 4, color: '#374151' }}>{t('listingDetail.email')} *</label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('listingDetail.emailPlaceholder')}
                    style={{
                      width: '100%',
                      padding: '0.6rem 0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: 8,
                      fontSize: '0.9rem',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: 4, color: '#374151' }}>{t('listingDetail.phone')}</label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder={t('listingDetail.phonePlaceholder')}
                    style={{
                      width: '100%',
                      padding: '0.6rem 0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: 8,
                      fontSize: '0.9rem',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: 4, color: '#374151' }}>{t('listingDetail.message')} *</label>
                  <textarea
                    required
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={4}
                    placeholder={t('listingDetail.messagePlaceholder')}
                    style={{
                      width: '100%',
                      padding: '0.6rem 0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: 8,
                      fontSize: '0.9rem',
                      resize: 'vertical',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting || listing.listingId.startsWith('lst-demo-')}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    background: '#0d9488',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    fontWeight: 600,
                    fontSize: '1rem',
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    opacity: submitting ? 0.7 : 1,
                  }}
                >
                  {submitting ? t('listingDetail.sending') : t('listingDetail.sendMessage')}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
